import { useState, type ImgHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TenantImage } from "@/components/home/TenantImage";
import { LoadingLabel, Skel, SkelLines } from "@/components/home/TenantSkeletons";
import type { HeroModel, ReviewCardItem } from "@/components/home/layouts/models";

/** Copy that replaces parts of a hero so the page editor can edit inline in every layout. */
export interface HeroEditSlots {
  title?: ReactNode;
  subtitle?: ReactNode;
  cta?: ReactNode;
  /** Fills its frame (position: absolute; inset: 0). */
  image?: ReactNode;
}

export interface HeroViewProps {
  m: HeroModel;
  edit?: HeroEditSlots;
}

/** Tasteful stand-in when a photo is missing or fails to load — never a broken image. */
export function PlaceholderArt({ className }: { className?: string }) {
  return (
    <div
      className={cn("absolute inset-0 flex items-center justify-center overflow-hidden", className)}
      style={{
        background:
          "radial-gradient(70% 70% at 25% 20%, hsl(var(--accent) / 0.35), transparent 70%), linear-gradient(135deg, hsl(var(--primary) / 0.28), hsl(var(--secondary)))",
      }}
      role="img"
      aria-label="Image coming soon"
    >
      <Sparkles className="h-1/4 w-1/4 max-h-16 max-w-16 text-primary/50" strokeWidth={1.25} aria-hidden />
    </div>
  );
}

/** Photo that fades in, and degrades to placeholder art if it can't load. */
export function Photo({ src, alt, className, ...rest }: ImgHTMLAttributes<HTMLImageElement>) {
  const [failedSrc, setFailedSrc] = useState<string | undefined>();
  if (!src || failedSrc === src) return <PlaceholderArt />;
  return <TenantImage src={src} alt={alt ?? ""} className={cn("absolute inset-0 h-full w-full object-cover", className)} onError={() => setFailedSrc(src)} {...rest} />;
}

export function StarRow({ n = 5, className }: { n?: number; className?: string }) {
  return (
    <span className={cn("tp-stars", className)} aria-hidden>
      {Array.from({ length: Math.max(0, Math.round(n)) }).map((_, i) => (
        <Star key={i} className="h-[1em] w-[1em] fill-current" strokeWidth={0} />
      ))}
    </span>
  );
}

/** Primary booking CTA + "Explore services" link, styled by variant CSS. */
export function HeroActions({ m, edit, className, ghostClassName, size = "xl" }: HeroViewProps & { className?: string; ghostClassName?: string; size?: "xl" | "lg" }) {
  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center", className)}>
      <Button variant="hero" size={size} className="tp-btn group h-14 px-8 text-base hover:translate-y-0" asChild={!edit?.cta}>
        {edit?.cta ? (
          <span className="pointer-events-none">{edit.cta}<span className="tp-btn-arrow"><ArrowRight /></span></span>
        ) : (
          <Link to={m.bookingHref}>
            {m.ctaText}
            <span className="tp-btn-arrow"><ArrowRight /></span>
          </Link>
        )}
      </Button>
      <Link to={m.servicesHref} className={cn("tp-btn-ghost text-base", ghostClassName)}>
        Explore services
      </Link>
    </div>
  );
}

export function HeroTitle({ m, edit, className, as: Tag = "h1" }: HeroViewProps & { className?: string; as?: "h1" | "h2" }) {
  return <Tag className={className}>{edit?.title ?? m.titleNode}</Tag>;
}

/** Skeleton copy block shown until the tenant's own hero is known — never paint the default headline first. */
export function HeroCopySkeleton({ className, align = "left" }: { className?: string; align?: "left" | "center" }) {
  return (
    <div className={cn("tp-skeleton", align === "center" && "mx-auto text-center", className)} aria-hidden>
      <LoadingLabel>Loading</LoadingLabel>
      <Skel className={cn("h-3 w-56 tp-skel-round", align === "center" && "mx-auto")} />
      <div className="tp-display mt-6 md:mt-7">
        <SkelLines lines={2} widths={["92%", "64%"]} />
      </div>
      <div className="tp-lead mt-6 max-w-[34rem] md:mt-7">
        <SkelLines lines={3} widths={["100%", "96%", "58%"]} />
      </div>
      <div className={cn("mt-9 flex gap-3", align === "center" && "justify-center")}>
        <Skel className="h-14 w-56 rounded-[var(--button-radius,var(--radius))]" />
      </div>
    </div>
  );
}

/** Rating line used by several heroes. */
export function RatingLine({ m, className }: { m: HeroModel; className?: string }) {
  if (m.reviewsPending) {
    return (
      <div className={cn("tp-skeleton flex items-center gap-4", className)} aria-hidden>
        <Skel className="h-4 w-36 tp-skel-round" />
        <Skel className="h-3.5 w-44 tp-skel-round" />
      </div>
    );
  }
  return (
    <div className={cn("tp-loaded flex flex-wrap items-center gap-x-4 gap-y-1", className)}>
      <span className="flex items-center gap-2">
        <span className="font-semibold text-foreground">{m.ratingLabel}</span>
        <StarRow n={5} className="text-[1.05rem]" />
      </span>
      <span className="text-sm text-muted-foreground">{m.ratingLine}</span>
    </div>
  );
}

function initials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

/** Reviewer avatar: Google photo when present, otherwise initials. */
export function Avatar({ item, className }: { item: ReviewCardItem; className?: string }) {
  if (item.photoUrl) {
    return <img src={item.photoUrl} alt="" referrerPolicy="no-referrer" loading="lazy" className={cn("shrink-0 rounded-full object-cover", className)} />;
  }
  return (
    <span className={cn("flex shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground", className)} aria-hidden>
      {initials(item.name)}
    </span>
  );
}

/** Reviewer name (linked to their Google profile when it's a Google review) + context line. */
export function ReviewerLine({ item, nameClassName, metaClassName }: { item: ReviewCardItem; nameClassName?: string; metaClassName?: string }) {
  const external = { target: "_blank", rel: "noopener noreferrer nofollow" } as const;
  return (
    <>
      <p className={cn("truncate text-sm font-semibold text-foreground", nameClassName)}>
        {item.google?.authorUrl ? <a href={item.google.authorUrl} {...external} className="hover:underline">{item.name}</a> : item.name}
      </p>
      <p className={cn("text-xs text-muted-foreground", metaClassName)}>
        {item.google ? (
          <>
            {item.meta ? `${item.meta} · ` : ""}
            <a href={item.google.reviewUrl} {...external} className="hover:underline">Posted on Google Maps</a>
          </>
        ) : item.meta}
      </p>
    </>
  );
}

/** Shared block for "Leave a review" / Google actions under a summary. */
export function ReviewExtras({ google, reviewFormHref, className }: {
  google: import("@/components/home/layouts/models").ReviewsModel["google"];
  reviewFormHref?: string;
  className?: string;
}) {
  if (!google && !reviewFormHref) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-x-5 gap-y-3", className)} data-testid={google ? "google-review-actions" : undefined}>
      {google && (
        <>
          <Button variant="hero" className="tp-btn h-11 px-5 text-sm hover:translate-y-0" asChild>
            <a href={google.place.writeReviewUrl} target="_blank" rel="noopener noreferrer">Leave us a Google review</a>
          </Button>
          <a href={google.place.reviewsUrl} target="_blank" rel="noopener noreferrer" className="tp-link text-sm">
            See all reviews on Google
          </a>
        </>
      )}
      {reviewFormHref && (
        <a href={reviewFormHref} className="tp-link text-sm">
          Leave a review <ArrowRight className="h-4 w-4" />
        </a>
      )}
    </div>
  );
}
